import { useCallback, useMemo } from 'react';

import dayjs from 'dayjs';

import {
  CalendarMeetingInfoDto,
  useListMeetingsInfo,
  useListSharedMeetingsInfo
} from '@/api/generated';
import { MeetingHubTabs } from '@/enums/MeetingHubTabs';
import { useMeetingHubFilters } from '@/pages/meetingsHub/MeetingHubFiltersContext';
import { defaultDateRange } from '@/pages/meetingsHub/util';

export const useMeetingsHub = ({
  searchMeetingValue,
  tabValue,
  setSearchValue
}: {
  searchMeetingValue: string;
  tabValue: MeetingHubTabs;
  setSearchValue?: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const {
    selectedDateRange,
    setSelectedDateRange,
    selectedParticipants,
    setSelectedParticipants,
    participantSearchValue,
    setParticipantSearchValue
  } = useMeetingHubFilters();

  const normalizeDateRange = (range?: { startDate?: number; endDate?: number }) => {
    const start = range?.startDate
      ? dayjs(range.startDate).startOf('day').valueOf()
      : defaultDateRange.startDate;

    const end = range?.endDate
      ? dayjs(range.endDate).endOf('day').valueOf()
      : dayjs(range?.startDate).endOf('day').valueOf();

    return { start, end };
  };

  const { start: normalizedStartDate, end: normalizedEndDate } =
    normalizeDateRange(selectedDateRange);

  const {
    data: meetingListResponse,
    isLoading: isLoadingMeetings,
    isFetching: isFetchingMeetings
  } = useListMeetingsInfo(
    {
      from: normalizedStartDate,
      to: normalizedEndDate
    },
    {
      query: {
        refetchOnWindowFocus: false
      }
    }
  );

  const {
    data: sharedMeetingListResponse,
    isLoading: isLoadingSharedMeetings,
    isFetching: isFetchingSharedMeetings
  } = useListSharedMeetingsInfo(
    {
      from: normalizedStartDate,
      to: normalizedEndDate
    },
    {
      query: {
        refetchOnWindowFocus: false
      }
    }
  );
  const myMeetings = useMemo(() => meetingListResponse?.items ?? [], [meetingListResponse]);
  const mySharedMeetings = useMemo(
    () => sharedMeetingListResponse?.items ?? [],
    [sharedMeetingListResponse]
  );

  const meetingsByTabValue = useMemo(
    () =>
      tabValue === MeetingHubTabs.All ? [...myMeetings, ...mySharedMeetings] : mySharedMeetings,
    [tabValue, myMeetings, mySharedMeetings]
  );

  const extractedParticipants = useMemo(() => {
    const uniqueParticipantsMap = new Map();

    meetingsByTabValue
      .flatMap(meeting => meeting.participants)
      .filter(Boolean)
      .forEach(participant => {
        if (!participant || !participant.email) return;
        if (!uniqueParticipantsMap.has(participant.email)) {
          uniqueParticipantsMap.set(participant.email, participant);
        }
      });

    return Array.from(uniqueParticipantsMap.values());
  }, [meetingsByTabValue]);

  const participants = useMemo(
    () =>
      extractedParticipants
        .map(participant => {
          if (!participant) return undefined;
          return {
            ...participant,
            isSelected: participant.email ? selectedParticipants.includes(participant.email) : false
          };
        })
        .filter(participant => {
          if (!participantSearchValue.length) return true;
          return participant?.email?.toLowerCase().includes(participantSearchValue.toLowerCase());
        })
        .filter(Boolean),
    [selectedParticipants, extractedParticipants, participantSearchValue]
  );

  const groupByWeekAndDay = (meetings: CalendarMeetingInfoDto[]) => {
    const weekGroups: Record<
      string,
      {
        weekKey: string;
        weekStartTimestamp: number;
        dayGroups: Record<
          string,
          {
            dayKey: string;
            dayTimestamp: number;
            meetings: CalendarMeetingInfoDto[];
          }
        >;
      }
    > = {};

    meetings.forEach(meeting => {
      if (!meeting.startTime) return;

      const meetingDate = dayjs(meeting.startTime);

      // Week grouping
      const weekStart = meetingDate.startOf('week');
      const weekStartStr = weekStart.format('YYYY-MM-DD');
      const weekEnd = meetingDate.endOf('week').format('YYYY-MM-DD');
      const weekKey = `${weekEnd} - ${weekStartStr}`;

      // Day grouping
      const dayKey = meetingDate.format('YYYY-MM-DD');
      const dayFormatted = meetingDate.format('dddd, MMMM D, YYYY');

      if (!weekGroups[weekKey]) {
        weekGroups[weekKey] = {
          weekKey,
          weekStartTimestamp: weekStart.valueOf(),
          dayGroups: {}
        };
      }

      if (!weekGroups[weekKey].dayGroups[dayKey]) {
        weekGroups[weekKey].dayGroups[dayKey] = {
          dayKey: dayFormatted,
          dayTimestamp: meetingDate.startOf('day').valueOf(),
          meetings: []
        };
      }

      weekGroups[weekKey].dayGroups[dayKey].meetings.push(meeting);
    });

    return weekGroups;
  };

  const filterMeetings = useCallback(
    (meetings: CalendarMeetingInfoDto[]) => {
      if (!selectedParticipants.length && !searchMeetingValue) return meetings;
      return meetings
        .filter(meeting => {
          if (!selectedParticipants.length) return true;
          return meeting.participants?.some(participant =>
            selectedParticipants.includes(participant.email ?? '')
          );
        })
        .filter(meeting => {
          if (!searchMeetingValue) return true;
          return meeting.title?.toLowerCase().includes(searchMeetingValue.toLowerCase());
        });
    },
    [selectedParticipants, searchMeetingValue]
  );

  const groupedMeetings = useMemo(() => {
    const filteredMeetings = filterMeetings(meetingsByTabValue);
    const weekGroups = groupByWeekAndDay(filteredMeetings);

    // Convert to array and sort weeks
    const result = Object.values(weekGroups)
      .map(weekGroup => {
        const sortedDayGroups = Object.values(weekGroup.dayGroups).sort(
          (a, b) => b.dayTimestamp - a.dayTimestamp
        );

        return {
          ...weekGroup,
          dayGroups: sortedDayGroups
        };
      })
      .sort((a, b) => b.weekStartTimestamp - a.weekStartTimestamp);

    return result;
  }, [meetingsByTabValue, filterMeetings]);

  const handleParticipantClick = (id: string) => {
    setSelectedParticipants(prevState => {
      if (prevState.includes(id)) {
        return prevState.filter(item => item !== id);
      }
      return [...prevState, id];
    });
  };

  const initFilters = () => {
    setSelectedParticipants([]);
    setSelectedDateRange(defaultDateRange);
    setSearchValue?.('');
  };

  return {
    handleParticipantClick,
    setSelectedParticipants,
    participants,
    groupedMeetings,
    selectedDateRange,
    setSelectedDateRange,
    initFilters,
    isLoading:
      isLoadingMeetings ||
      isFetchingMeetings ||
      isLoadingSharedMeetings ||
      isFetchingSharedMeetings,
    selectedParticipants,
    isEmptyGroupedMeetings: !Object.keys(groupedMeetings).length,
    participantSearchValue,
    setParticipantSearchValue
  };
};
